import { useState } from '@hookstate/core';
import { useState as classicUseState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { SearchBarExpansiveFieldConfigWithHeader } from '@components/SearchBars';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClickAwayListener } from '@material-ui/core';
import { ICustomField } from '@models/CustomField.model';
import FieldConfiguration, {
  EquipmentFieldConfigurationTypeOptions,
  FieldConfigurationLocationOptions,
  IFieldConfiguration,
} from '@models/FieldConfiguration.model';
import { MaybeUndef } from '@pages/Asset/components/Legacy/AddField/AddField.types';
import { findEquipmentField } from '@pages/Asset/components/Legacy/AddField/AddField.utils';
import {
  BlackIcon,
  Container,
  FlexRowContainer,
} from './EditableFieldConfig.styles';

interface InfoEditableProps {
  fieldConfiguration: IFieldConfiguration;
  options: string[];
  optionsCustom: string[];
  onClickTrash: () => void;
  customFields?: ICustomField[];
  fieldsConfiguration: IFieldConfiguration[];
  updateFieldConfiguration: any;
  index: number;
  location: FieldConfigurationLocationOptions;
}
const EditableFieldConfig = ({
  fieldConfiguration,
  options,
  onClickTrash,
  index,
  customFields,
  location,
  fieldsConfiguration,
  updateFieldConfiguration,
  optionsCustom,
}: InfoEditableProps) => {
  const open = useState(false);
  const [label, setLabel] = classicUseState(fieldConfiguration.name);
  const [showOptions, setShowOptions] = classicUseState<string[]>([]);
  const [showCustomOptions, setShowCustomOptions] = classicUseState<string[]>(
    [],
  );

  const handleClickIn = () => {
    open.set(true);
    setShowOptions(options);
    setShowCustomOptions(optionsCustom);
  };

  const handleClickOut = async () => {
    open.set(false);
    setShowOptions([]);
    setShowCustomOptions([]);
  };

  const handleOnChangeSelect = (event: string) => {
    setLabel(event);
    const equipmentField = findEquipmentField(
      location,
      event,
    ) as MaybeUndef<EquipmentFieldConfigurationTypeOptions>;
    const updatedConfig = fieldsConfiguration;
    if (equipmentField) {
      updatedConfig[index] = FieldConfiguration.fromApiModel(
        {
          id: uuidv4(),
          location,
          order: index,
          fieldName: equipmentField,
        },
        location,
      );
      updateFieldConfiguration(updatedConfig);
    } else {
      const customFieldFound = customFields?.find(
        field => field.name === event,
      );
      if (customFieldFound) {
        updatedConfig[index] = FieldConfiguration.fromApiModel(
          {
            order: index,
            id: customFieldFound.id,
            location,
            customField: {
              id: customFieldFound.id,
              name: customFieldFound.name,
              type: customFieldFound.type,
              studioConfigurationId: '',
              isUnique: false,
              isSearchable: false,
              fieldContents: [],
              selectableValues: [],
              defaultValue: customFieldFound.defaultValue,
            },
          },
          location,
        );
        updateFieldConfiguration(updatedConfig);
      }
    }
    setShowOptions([]);
    setShowCustomOptions([]);
  };

  const handleClickAway = () => {
    handleClickOut();
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <FlexRowContainer>
        <Container
          data-testid="field-config-opt"
          isHidden={!open.get()}
          isNotDate
        >
          <SearchBarExpansiveFieldConfigWithHeader
            showOptions={showOptions}
            showCustomOptions={showCustomOptions}
            searchText={label}
            handleClickSearch={handleClickIn}
            isHidden={!open.get()}
            onClickFilter={input => handleOnChangeSelect(input)}
          />
          <BlackIcon
            isHidden={open.get()}
            onClick={() =>
              updateFieldConfiguration(
                fieldsConfiguration.filter(
                  field => field.id !== fieldConfiguration.id,
                ),
              )
            }
          >
            <FontAwesomeIcon
              data-testid="field-config-opt-delete"
              icon={faTrash}
              onClick={onClickTrash}
            />
          </BlackIcon>
        </Container>
      </FlexRowContainer>
    </ClickAwayListener>
  );
};
export { EditableFieldConfig };
